/**
 * Velocity: quantity per store per period — the number brands actually argue about when they
 * adjust an item-level forecast.
 *
 * The grid stores store count and quantity separately, so velocity is derived rather than
 * persisted: `velocity = quantity ÷ stores`. Editing it is the inverse — the store count stays
 * put and the quantity moves, which is what "we think this does 14 units a store, not 11" means.
 * Keeping it derived is deliberate: a stored velocity would immediately disagree with the
 * quantity the first time anyone edited either one directly.
 */

/** Velocity is shown to this many decimals; below ~1/store the extra digit is the whole signal. */
export const VELOCITY_DECIMALS = 2

/**
 * Quantity per store. `null` when there are no stores — a velocity over zero doors is not zero,
 * it is undefined, and rendering it as `0.00` invites someone to "fix" a number that isn't wrong.
 */
export function compute_velocity(quantity: number | null | undefined, store_count: number): number | null {
  if (quantity == null || !Number.isFinite(quantity)) return null
  if (!Number.isFinite(store_count) || store_count <= 0) return null
  return quantity / store_count
}

/**
 * The quantity implied by a velocity, rounded to a whole unit/case because that is what the
 * columns hold. Returns `null` when the input can't move the forecast — no stores, or a
 * negative/unparseable velocity — so callers skip the write instead of zeroing the cell.
 */
export function quantity_from_velocity(velocity: number, store_count: number): number | null {
  if (!Number.isFinite(velocity) || velocity < 0) return null
  if (!Number.isFinite(store_count) || store_count <= 0) return null
  return Math.round(velocity * store_count)
}

/** Display form: `12.40`, or an em dash when velocity is undefined. */
export function format_velocity(value: number | null): string {
  return value == null ? "—" : value.toFixed(VELOCITY_DECIMALS)
}

/**
 * Whether a committed edit is a real change.
 *
 * Velocity is displayed rounded, so re-committing what is on screen would otherwise write a
 * slightly different quantity every time a cell is focused and blurred.
 */
export function velocity_edit_changes_quantity(
  next_velocity: number,
  current_quantity: number | null | undefined,
  store_count: number
): boolean {
  const next_quantity = quantity_from_velocity(next_velocity, store_count)
  if (next_quantity == null) return false
  return next_quantity !== Math.round(current_quantity ?? 0)
}
